import * as React from "react"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"

export function MarketingMotion({ children }: { children: React.ReactNode }) {
  const scope = React.useRef<HTMLDivElement>(null)

  useGSAP(() => {
    if (typeof window === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    let cancelled = false
    void import("gsap/ScrollTrigger").then(({ ScrollTrigger }) => {
      if (cancelled || !scope.current) return
      gsap.registerPlugin(ScrollTrigger)

      gsap.fromTo("[data-hero-copy] > *", { y: 38, opacity: 0 }, {
        y: 0,
        opacity: 1,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
        clearProps: "transform,opacity",
      })

      gsap.utils.toArray<HTMLElement>("[data-scale-media]").forEach((element) => {
        gsap.fromTo(element, { scale: 0.82, opacity: 0.35 }, {
          scale: 1,
          opacity: 1,
          ease: "none",
          scrollTrigger: { trigger: element, start: "top 92%", end: "center 48%", scrub: 0.8 },
        })
        gsap.to(element, {
          opacity: 0.2,
          filter: "brightness(.5)",
          ease: "none",
          scrollTrigger: { trigger: element, start: "bottom 42%", end: "bottom 10%", scrub: 0.8 },
        })
      })

      gsap.utils.toArray<HTMLElement>("[data-stack-card]").forEach((card, index) => {
        gsap.fromTo(card, { y: 90, scale: 0.94 }, {
          y: index * -14,
          scale: 1,
          ease: "none",
          scrollTrigger: { trigger: card, start: "top 92%", end: "top 28%", scrub: 0.9 },
        })
      })

      gsap.utils.toArray<HTMLElement>("[data-pin-title]").forEach((title) => {
        const section = title.closest<HTMLElement>("[data-pin-section]")
        if (!section || window.innerWidth < 1024) return
        ScrollTrigger.create({
          trigger: section,
          start: "top 112px",
          end: "bottom bottom-=120",
          pin: title,
          pinSpacing: false,
        })
      })

      gsap.utils.toArray<HTMLElement>("[data-reveal-copy]").forEach((element) => {
        gsap.fromTo(element, { opacity: 0.14 }, {
          opacity: 1,
          ease: "none",
          scrollTrigger: { trigger: element, start: "top 82%", end: "bottom 48%", scrub: 0.8 },
        })
      })
    })

    return () => {
      cancelled = true
      void import("gsap/ScrollTrigger").then(({ ScrollTrigger }) => ScrollTrigger.getAll().forEach((trigger) => trigger.kill()))
    }
  }, { scope })

  return <div ref={scope}>{children}</div>
}

export function Marquee({ items }: { items: string[] }) {
  const repeated = [...items, ...items]
  return (
    <div className="overflow-hidden border-y border-marketing-ink/12 py-5" aria-label={items.join(", ")}>
      <div className="marketing-marquee flex w-max items-center gap-10 whitespace-nowrap" aria-hidden>
        {repeated.map((item, index) => (
          <React.Fragment key={`${item}-${index}`}>
            <span className="font-marketing text-sm font-semibold uppercase tracking-[0.13em] text-marketing-ink/52">{item}</span>
            <span className="h-1.5 w-1.5 bg-marketing-oxide" />
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export function QuoteCarousel({ quotes }: { quotes: { quote: string; name: string; role: string }[] }) {
  const [current, setCurrent] = React.useState(0)
  const quote = quotes[current]
  return (
    <div className="grid gap-10 bg-white p-7 shadow-[0_30px_90px_rgba(18,31,26,.08)] sm:p-10 lg:grid-cols-[1fr_auto] lg:items-end">
      <div>
        <blockquote className="border-0 p-0 font-marketing text-[clamp(1.8rem,3vw,3.6rem)] font-medium leading-[1.04] tracking-[-0.045em] text-marketing-ink not-italic">“{quote.quote}”</blockquote>
        <div className="mt-10 flex items-center gap-4">
          <div className="flex -space-x-3" aria-hidden>
            {quotes.slice(0, 3).map((_, index) => <img key={index} src={`https://picsum.photos/seed/contractor-portrait-${index}/96/96`} alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover grayscale" />)}
          </div>
          <div><p className="text-sm font-semibold text-marketing-ink">{quote.name}</p><p className="text-xs text-marketing-ink/48">{quote.role}</p></div>
        </div>
      </div>
      <div className="flex gap-2">
        {quotes.map((_, index) => <button key={index} type="button" className={`h-2 transition-[width,background-color] ${index === current ? "w-10 bg-marketing-oxide" : "w-5 bg-marketing-ink/15"}`} onClick={() => setCurrent(index)} aria-label={`Show quote ${index + 1}`} aria-current={index === current} />)}
      </div>
    </div>
  )
}
